import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { switchChurchAndOpen } from '@/lib/switch-church-workspace';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';

import { AuthLayout } from './auth-layout';

interface InvitePreview {
  churchName: string;
  email: string;
  systemRole: string;
  expiresAt: string;
  hasAccount: boolean;
  isExpired: boolean;
  isPending: boolean;
}

/**
 * Landing page for the link in a church invite email.
 *
 * People with an account sign in (if needed) then accept. People without one create
 * an account with the invited email; registration consumes the token.
 */
export const AcceptInvitePage = () => {
  const { t } = useTranslation('auth');
  const { t: tChurches } = useTranslation('churches');
  const { t: tErrors } = useTranslation('errors');
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const selectChurch = useChurchStore((state) => state.selectChurch);
  const restore = useChurchStore((state) => state.restore);
  const accepted = useRef(false);

  const previewQuery = useQuery({
    queryKey: ['church-invite-preview', token],
    queryFn: () =>
      api.get<InvitePreview>('/church-invites/preview', {
        query: { token: token! },
        withoutChurch: true,
      }),
    enabled: Boolean(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      api.post<{ churchId: string; membershipId: string }>(
        '/church-invites/accept',
        { token },
        { withoutChurch: true },
      ),
    onSuccess: async (result) => {
      toast.success(
        t('acceptInvite.success', {
          churchName: previewQuery.data?.churchName ?? '',
        }),
      );
      await switchChurchAndOpen({
        action: async () => {
          await restore();
          await selectChurch(result.churchId);
        },
        navigate,
        mode: 'join',
        churchName: previewQuery.data?.churchName,
      });
    },
  });

  const preview = previewQuery.data;
  const emailMatches =
    user && preview
      ? user.email.trim().toLowerCase() === preview.email.trim().toLowerCase()
      : false;

  useEffect(() => {
    if (!preview?.isPending || status !== 'authenticated' || !emailMatches) return;
    if (accepted.current || accept.isPending || accept.isSuccess) return;
    accepted.current = true;
    accept.mutate();
  }, [preview, status, emailMatches, accept]);

  if (!token) {
    return (
      <AuthLayout title={t('acceptInvite.title')} description={t('acceptInvite.invalid')}>
        <Button asChild>
          <Link to="/sign-in">{t('signIn.submit')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  if (previewQuery.isLoading) {
    return (
      <AuthLayout title={t('acceptInvite.title')} description={t('acceptInvite.loading')}>
        <Spinner />
      </AuthLayout>
    );
  }

  if (previewQuery.isError || !preview) {
    const message =
      previewQuery.error instanceof ApiError
        ? tErrors(previewQuery.error.code, previewQuery.error.params)
        : t('acceptInvite.invalid');
    return (
      <AuthLayout title={t('acceptInvite.title')} description={message}>
        <Button asChild>
          <Link to="/sign-in">{t('signIn.submit')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  if (!preview.isPending) {
    return (
      <AuthLayout
        title={t('acceptInvite.title')}
        description={preview.isExpired ? t('acceptInvite.expired') : t('acceptInvite.invalid')}
      >
        <Button asChild>
          <Link to="/sign-in">{t('signIn.submit')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  const roleLabel = tChurches(`membership.role.${preview.systemRole}`);
  const description = t('acceptInvite.description', {
    churchName: preview.churchName,
    role: roleLabel,
  });

  if (status === 'authenticated' && !emailMatches) {
    return (
      <AuthLayout
        title={t('acceptInvite.title')}
        description={t('acceptInvite.wrongAccount', { email: preview.email })}
      >
        <p className="text-body-sm text-fg-muted">{description}</p>
      </AuthLayout>
    );
  }

  if (status === 'authenticated' && emailMatches) {
    if (accept.isError) {
      return (
        <AuthLayout title={t('acceptInvite.title')} description={description}>
          <p className="text-body-sm text-danger">
            {accept.error instanceof ApiError
              ? tErrors(accept.error.code, accept.error.params)
              : t('acceptInvite.invalid')}
          </p>
          <Button className="mt-4" onClick={() => accept.mutate()}>
            {t('acceptInvite.accept')}
          </Button>
        </AuthLayout>
      );
    }

    return null;
  }

  const returnTo = `/accept-invite?token=${encodeURIComponent(token)}`;
  const registerTo = `/register?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(preview.email)}`;
  const signInTo = `/sign-in?returnTo=${encodeURIComponent(returnTo)}`;

  return (
    <AuthLayout title={t('acceptInvite.title')} description={description}>
      <p className="mb-4 text-body-sm text-fg-muted">
        {preview.hasAccount
          ? t('acceptInvite.signInPrompt', { email: preview.email })
          : t('acceptInvite.registerPrompt', { email: preview.email })}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        {preview.hasAccount ? (
          <Button asChild>
            <Link to={signInTo}>{t('acceptInvite.signIn')}</Link>
          </Button>
        ) : (
          <Button asChild>
            <Link to={registerTo}>{t('acceptInvite.register')}</Link>
          </Button>
        )}
        {preview.hasAccount ? null : (
          <Button variant="outline" asChild>
            <Link to={signInTo}>{t('acceptInvite.signIn')}</Link>
          </Button>
        )}
      </div>
    </AuthLayout>
  );
};
